/**
 * Sound Registry for Candy Animations
 * 
 * Maps animation phases to sound files.
 * Sounds are organized by intensity tier (1-6).
 * Higher tiers get additional sounds layered on top.
 */

// Import paths are relative to the animsounds directory
const SOUND_BASE_PATH = './animsounds';

/**
 * Sound configuration per candy type
 * Structure: { phase: { baseSounds: [], tierSounds: { tier: [sounds] } } }
 */
export const SOUND_REGISTRY = {
    CocoaPop: {
        materializing: {
            base: ['01_CocoaPop_Chocolate_Bomb/01_materializing_magic_sparkle.mp3'],
        },
        rumbling: {
            base: ['01_CocoaPop_Chocolate_Bomb/02_rumbling_volcano.mp3'],
        },
        cracking: {
            base: ['01_CocoaPop_Chocolate_Bomb/03_cracking_shell.mp3'],
        },
        popping: {
            base: ['01_CocoaPop_Chocolate_Bomb/04_popping_explosion.mp3'],
            tier2: ['01_CocoaPop_Chocolate_Bomb/05_comic_pop_text.mp3'],
            tier4: ['01_CocoaPop_Chocolate_Bomb/08_vine_boom.mp3'],
            tier5: ['01_CocoaPop_Chocolate_Bomb/06_goat_scream.mp3'],
            tier6: ['01_CocoaPop_Chocolate_Bomb/07_bruh_sound.mp3', '01_CocoaPop_Chocolate_Bomb/09_taco_bell_bong.mp3'],
        },
    },

    // DryPull uses flow-based system - see DRYPULL_FLOWS below
    DryPull: null,

    NutPop: {
        descending: {
            base: ['03_NutPop_Treasure_Eruption/01_glassy_descend.mp3'],
        },
        pressuring: {
            base: ['03_NutPop_Treasure_Eruption/02_pressure_steam.mp3'],
        },
        cracking: {
            base: ['03_NutPop_Treasure_Eruption/03_cracking_shell.mp3'],
        },
        exploding: {
            base: ['03_NutPop_Treasure_Eruption/04_treasure_burst.mp3'],
            tier3: ['03_NutPop_Treasure_Eruption/10_mario_coins.mp3'],
            tier4: ['03_NutPop_Treasure_Eruption/07_mlg_airhorn.mp3'],
        },
        celebrating: {
            base: ['03_NutPop_Treasure_Eruption/05_victory_fanfare.mp3'],
            tier4: ['03_NutPop_Treasure_Eruption/06_slot_jackpot.mp3'],
            tier5: ['03_NutPop_Treasure_Eruption/08_oh_baby_triple.mp3'],
            tier6: ['03_NutPop_Treasure_Eruption/09_money_money.mp3'],
        },
    },

    ShaftBerry: {
        charging: {
            base: ['04_ShaftBerry_Neon_Overload/01_electric_charging.mp3'],
        },
        pulsing: {
            base: ['04_ShaftBerry_Neon_Overload/02_bass_pulse.mp3'],
        },
        revealing: {
            base: ['04_ShaftBerry_Neon_Overload/03_electric_zap.mp3'],
            tier3: ['04_ShaftBerry_Neon_Overload/09_lightsaber.mp3'],
        },
        energizing: {
            base: ['04_ShaftBerry_Neon_Overload/04_lightning_laser.mp3'],
            tier2: ['04_ShaftBerry_Neon_Overload/05_comic_zap.mp3'],
            tier4: ['04_ShaftBerry_Neon_Overload/08_electro_shock.mp3'],
            tier5: ['04_ShaftBerry_Neon_Overload/06_matrix_rain.mp3', '04_ShaftBerry_Neon_Overload/07_dbz_powerup.mp3'],
            tier6: ['04_ShaftBerry_Neon_Overload/10_wifi_connect.mp3', '04_ShaftBerry_Neon_Overload/11_unlimited_power.mp3'],
        },
    },

    SlapTaffy: {
        winding: {
            base: ['05_SlapTaffy_Slap_Attack/01_winding_whoosh.mp3'],
        },
        slapping: {
            base: ['05_SlapTaffy_Slap_Attack/02_slapping_whoosh.mp3'],
        },
        impact: {
            base: ['05_SlapTaffy_Slap_Attack/03_impact_slap.mp3'],
            tier2: ['05_SlapTaffy_Slap_Attack/05_comic_slap_variation.mp3'],
            tier5: ['05_SlapTaffy_Slap_Attack/06_combo_counter.mp3'],
        },
        wobbling: {
            base: ['05_SlapTaffy_Slap_Attack/04_wobbling_boing.mp3'],
        },
    },

    CandyWrap: {
        snatching: {
            base: ['05_SlapTaffy_Slap_Attack/01_winding_whoosh.mp3'],
        },
        cranking: {
            base: ['04_ShaftBerry_Neon_Overload/02_bass_pulse.mp3'],
        },
        stamping: {
            base: ['05_SlapTaffy_Slap_Attack/03_impact_slap.mp3'],
            tier2: ['05_SlapTaffy_Slap_Attack/05_comic_slap_variation.mp3'],
            tier5: ['05_SlapTaffy_Slap_Attack/06_combo_counter.mp3'],
        },
        bursting: {
            base: ['01_CocoaPop_Chocolate_Bomb/04_popping_explosion.mp3'],
            tier3: ['03_NutPop_Treasure_Eruption/04_treasure_burst.mp3'],
            tier4: ['01_CocoaPop_Chocolate_Bomb/05_comic_pop_text.mp3'],
        },
        victory: {
            base: ['03_NutPop_Treasure_Eruption/05_victory_fanfare.mp3'],
            tier4: ['03_NutPop_Treasure_Eruption/06_slot_jackpot.mp3'],
            tier6: ['03_NutPop_Treasure_Eruption/09_money_money.mp3'],
        },
    },
};

/**
 * DryPull Flow Definitions
 * 
 * 5 themed sound flows for variety - randomly selected per animation.
 * Each flow has sounds for 4 phases: hopeful, revealing, crumpling, drifting
 */
export const DRYPULL_FLOWS = {
    // Flow 0: Original classic sad sounds
    original: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/1/01_hopeful_music_sting.mp3'],
        },
        revealing: {
            base: ['02_DryPull_Sad_Moment/1/02_question_bing.mp3'],
        },
        crumpling: {
            base: ['02_DryPull_Sad_Moment/1/03_sad_trombone_crumple.mp3'],
            tier2: ['02_DryPull_Sad_Moment/1/05_tear_drop_plop.mp3'],
        },
        drifting: {
            base: ['02_DryPull_Sad_Moment/1/04_wind_tumbleweed.mp3'],
            tier2: ['02_DryPull_Sad_Moment/1/06_sad_violin.mp3'],
            tier3: ['02_DryPull_Sad_Moment/1/07_oof_sound.mp3'],
            tier4: ['02_DryPull_Sad_Moment/1/08_windows_shutdown.mp3'],
            tier5: ['02_DryPull_Sad_Moment/1/09_price_is_right_horn.mp3'],
            tier6: ['02_DryPull_Sad_Moment/1/10_hello_darkness.mp3', '02_DryPull_Sad_Moment/1/11_oh_no_table.mp3'],
        },
    },

    // Flow 1: Toilet/Flush themed 🚽
    flush: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/flow1_flush/01_toilet_fill_buildup.mp3'],
        },
        revealing: {
            base: ['02_DryPull_Sad_Moment/flow1_flush/02_flush_whoosh.mp3'],
        },
        crumpling: {
            base: ['02_DryPull_Sad_Moment/flow1_flush/03_splash_bubbles.mp3'],
            tier2: ['02_DryPull_Sad_Moment/flow1_flush/05_wet_fart_tier2.mp3'],
        },
        drifting: {
            base: ['02_DryPull_Sad_Moment/flow1_flush/04_empty_bowl_echo.mp3'],
            tier3: ['02_DryPull_Sad_Moment/flow1_flush/06_sewer_monster_tier3.mp3'],
            tier4: ['02_DryPull_Sad_Moment/flow1_flush/07_plunger_pop_tier4.mp3'],
            tier5: ['02_DryPull_Sad_Moment/flow1_flush/08_tp_roll_empty_tier5.mp3'],
            tier6: ['02_DryPull_Sad_Moment/flow1_flush/09_fan_death_tier6.mp3'],
        },
    },

    // Flow 2: Crypto Rugpull themed 🏃
    rugpull: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/flow2_rugpull/01_villain_buildup.mp3'],
        },
        revealing: {
            base: ['02_DryPull_Sad_Moment/flow2_rugpull/02_rug_rip_swipe.mp3'],
        },
        crumpling: {
            base: ['02_DryPull_Sad_Moment/flow2_rugpull/03_falling_scream.mp3'],
            tier2: ['02_DryPull_Sad_Moment/flow2_rugpull/05_empty_register_tier2.mp3'],
        },
        drifting: {
            base: ['02_DryPull_Sad_Moment/flow2_rugpull/04_empty_wallet_wind.mp3'],
            tier3: ['02_DryPull_Sad_Moment/flow2_rugpull/06_snake_hiss_tier3.mp3'],
            tier4: ['02_DryPull_Sad_Moment/flow2_rugpull/07_police_siren_tier4.mp3'],
            tier5: ['02_DryPull_Sad_Moment/flow2_rugpull/08_villain_laugh_fade_tier5.mp3'],
            tier6: ['02_DryPull_Sad_Moment/flow2_rugpull/09_cockroach_scatter_tier6.mp3'],
        },
    },

    // Flow 3: Deflated ego themed 📉
    deflated: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/flow3_deflated/01_macho_buildup.mp3'],
        },
        revealing: {
            base: ['02_DryPull_Sad_Moment/flow3_deflated/02_deflate_raspberry.mp3'],
        },
        crumpling: {
            base: ['02_DryPull_Sad_Moment/flow3_deflated/03_tiny_squeak.mp3'],
            tier2: ['02_DryPull_Sad_Moment/flow3_deflated/05_slide_whistle_tier2.mp3'],
        },
        drifting: {
            base: ['02_DryPull_Sad_Moment/flow3_deflated/04_crickets_silence.mp3'],
            tier3: ['02_DryPull_Sad_Moment/flow3_deflated/06_dog_whimper_tier3.mp3'],
            tier4: ['02_DryPull_Sad_Moment/flow3_deflated/07_air_horn_fail_tier4.mp3'],
            tier5: ['02_DryPull_Sad_Moment/flow3_deflated/08_tiny_violin_tier5.mp3'],
            tier6: ['02_DryPull_Sad_Moment/flow3_deflated/09_micro_boing_tier6.mp3'],
        },
    },

    // Flow 4: Horny jail / Bonk themed 🔨
    bonk: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/flow4_bonk/01_horny_breathing.mp3'],
        },
        revealing: {
            base: ['02_DryPull_Sad_Moment/flow4_bonk/02_bonk_hit.mp3'],
        },
        crumpling: {
            base: ['02_DryPull_Sad_Moment/flow4_bonk/03_dazed_chirps.mp3'],
            tier2: ['02_DryPull_Sad_Moment/flow4_bonk/05_rejection_horn_tier2.mp3'],
        },
        drifting: {
            base: ['02_DryPull_Sad_Moment/flow4_bonk/04_jail_door_slam.mp3'],
            tier3: ['02_DryPull_Sad_Moment/flow4_bonk/06_chain_rattle_tier3.mp3'],
            tier4: ['02_DryPull_Sad_Moment/flow4_bonk/07_buzzer_wrong_tier4.mp3'],
            tier5: ['02_DryPull_Sad_Moment/flow4_bonk/08_crying_club_tier5.mp3'],
            tier6: ['02_DryPull_Sad_Moment/flow4_bonk/09_lock_click_tier6.mp3'],
        },
    },

    // Flow 5: Homer Simpson "D'oh!" moment 🍩
    // Single sound that plays when animation starts
    homer: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/Homer.m4a'],
        },
        revealing: { base: [] },
        crumpling: { base: [] },
        drifting: { base: [] },
    },

    // Flow 6: Classic sad horn / Price is Wrong 🎺
    // Single sound that plays when animation starts
    sad_horn: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/sad_horn.m4a'],
        },
        revealing: { base: [] },
        crumpling: { base: [] },
        drifting: { base: [] },
    },

    // Flow 7: Spongebob sad moment 🧽
    // Single sound that plays when animation starts
    spongebob: {
        hopeful: {
            base: ['02_DryPull_Sad_Moment/Spongebob Sad - Sound Effect(HD).mp3'],
        },
        revealing: { base: [] },
        crumpling: { base: [] },
        drifting: { base: [] },
    },
};

// Flow keys for random selection
const DRYPULL_FLOW_KEYS = Object.keys(DRYPULL_FLOWS);

/**
 * Get a random DryPull flow key
 * @returns {string} Random flow key (original, flush, rugpull, deflated, bonk)
 */
export const getRandomDryPullFlow = () => {
    const index = Math.floor(Math.random() * DRYPULL_FLOW_KEYS.length);
    return DRYPULL_FLOW_KEYS[index];
};

/**
 * Get sounds for a DryPull phase from a specific flow
 * @param {string} flowKey - The flow key (original, flush, etc.)
 * @param {string} phase - The animation phase
 * @param {number} intensity - The intensity level (1-6)
 * @returns {string[]} Array of sound file paths
 */
export const getDryPullFlowSounds = (flowKey, phase, intensity = 1) => {
    const flow = DRYPULL_FLOWS[flowKey];
    if (!flow || !flow[phase]) return [];

    const phaseSounds = flow[phase];
    const sounds = [...(phaseSounds.base || [])];

    // Add tier-specific sounds based on intensity
    for (let tier = 2; tier <= intensity; tier++) {
        const tierKey = `tier${tier}`;
        if (phaseSounds[tierKey]) {
            sounds.push(...phaseSounds[tierKey]);
        }
    }

    // Prepend base path
    return sounds.map(s => `${SOUND_BASE_PATH}/${s}`);
};

/**
 * Get all sounds for a phase at a given intensity
 * @param {string} candyType - The candy type (CocoaPop, DryPull, etc.)
 * @param {string} phase - The animation phase
 * @param {number} intensity - The intensity level (1-6)
 * @param {string} flowKey - Optional flow key for DryPull
 * @returns {string[]} Array of sound file paths
 */
export const getSoundsForPhase = (candyType, phase, intensity = 1, flowKey = null) => {
    // Special handling for DryPull with flows
    if (candyType === 'DryPull') {
        const flow = flowKey || 'original';
        return getDryPullFlowSounds(flow, phase, intensity);
    }

    const candySounds = SOUND_REGISTRY[candyType];
    if (!candySounds || !candySounds[phase]) return [];

    const phaseSounds = candySounds[phase];
    const sounds = [...(phaseSounds.base || [])];

    // Add tier-specific sounds based on intensity
    for (let tier = 2; tier <= intensity; tier++) {
        const tierKey = `tier${tier}`;
        if (phaseSounds[tierKey]) {
            sounds.push(...phaseSounds[tierKey]);
        }
    }

    // Prepend base path
    return sounds.map(s => `${SOUND_BASE_PATH}/${s}`);
};

/**
 * Get phases for a candy type
 * @param {string} candyType - The candy type
 * @returns {string[]} Array of phase names in order
 */
export const getPhasesForCandy = (candyType) => {
    const phases = {
        CocoaPop: ['materializing', 'rumbling', 'cracking', 'popping'],
        DryPull: ['hopeful', 'revealing', 'crumpling', 'drifting'],
        NutPop: ['descending', 'pressuring', 'cracking', 'exploding', 'celebrating'],
        ShaftBerry: ['charging', 'pulsing', 'revealing', 'energizing'],
        SlapTaffy: ['winding', 'slapping', 'impact', 'wobbling'],
        CandyWrap: ['snatching', 'cranking', 'stamping', 'bursting', 'victory'],
    };
    return phases[candyType] || [];
};

export default SOUND_REGISTRY;

